// ENG-3451: account-collision signal parsing.
//
// When Auth0 rejects a sign-in because the email already belongs to an
// account on a DIFFERENT connection (e.g. Google sign-in attempted against an
// email/password account, or vice versa), the rejecting
// surface hands the frontend an error_description of the verified shape
// `COLLISION:<Auth0 connection>`. This module is the single parser for that
// signal, shared by:
//   - the web Auth0 redirect handling (auth/provider.tsx),
//   - the Electron hosted-auth error handoff (electron/api.ts `auth:error`),
//   - the collision page itself (routes/login/collision.tsx).
//
// Security contract: the raw error_description is server-controlled and must
// NEVER be rendered. Only the mapped friendly labels below reach the UI, and
// unknown/malicious connections degrade to a fixed safe fallback.
// Per rule: code-comments-required + reference-rules-in-comments
// (ai.knowledge/global).

import { Paths } from '#~/routes/constants.js';

/** Verified wire prefix emitted by the Auth0 collision signal. */
export const COLLISION_ERROR_PREFIX = 'COLLISION:';

/**
 * Auth0 connections the collision page has dedicated, friendly copy for.
 * `unknown` is the safe fallback for any other (or malformed) connection.
 */
export type CollisionConnection =
  | 'google-oauth2'
  | 'Username-Password-Authentication'
  | 'iris-itg-db'
  | 'iris-pro-db'
  | 'unknown';

// Email/password accounts live on each tenant's database connection: Auth0's
// default name, or iris-itg-db (Iris STG/ITG) and iris-pro-db (Iris prod).
const KNOWN_CONNECTIONS: ReadonlySet<string> = new Set([
  'google-oauth2',
  'Username-Password-Authentication',
  'iris-itg-db',
  'iris-pro-db'
]);

/**
 * Parse an Auth0 error_description into a collision connection.
 *
 * Returns `null` when the description is not a collision signal at all (the
 * prefix must match at position 0 — an embedded prefix mid-string is not a
 * signal). Returns `'unknown'` when the prefix matches but the connection is
 * empty, unrecognized, or malicious — strict whole-string matching means a
 * look-alike like `COLLISION:google-oauth2<payload>` can never resolve to a
 * known connection.
 *
 * @param errorDescription - Auth0-controlled error_description value.
 * @returns The collision connection, or null when not a collision.
 */
export const parseCollisionConnection = (errorDescription?: string): CollisionConnection | null => {
  if (!errorDescription || !errorDescription.startsWith(COLLISION_ERROR_PREFIX)) {
    return null;
  }

  const connection = errorDescription.slice(COLLISION_ERROR_PREFIX.length);

  return KNOWN_CONNECTIONS.has(connection) ? (connection as CollisionConnection) : 'unknown';
};

/**
 * Friendly, user-facing label for a collision connection. Fixed strings only
 * — no server-controlled content — so the label can never become an
 * injection vector.
 *
 * @param connection - Parsed collision connection.
 * @returns User-facing sign-in method label.
 */
export const getCollisionProviderLabel = (connection: CollisionConnection): string => {
  switch (connection) {
    case 'google-oauth2':
      return 'Google';
    case 'Username-Password-Authentication':
    case 'iris-itg-db':
    case 'iris-pro-db':
      return 'email and password';
    default:
      return 'the sign-in method you originally used';
  }
};

/**
 * Build the collision route (with the collision parameters URL-encoded into
 * the query string) for a failed auth handoff, or `null` when the failure is
 * not a collision — letting the caller keep its existing error handling.
 * Shared by the web redirect handling and the Electron hosted-auth handoff
 * so both surfaces deep-link to the same route with the same parameters.
 *
 * @param args.error - Optional Auth0 error code (e.g. 'access_denied').
 * @param args.errorDescription - Auth0-controlled error_description value.
 * @returns Collision route path, or null when not a collision.
 */
export const buildCollisionPath = ({
  error,
  errorDescription
}: {
  error?: string;
  errorDescription?: string;
}): string | null => {
  if (!parseCollisionConnection(errorDescription) || !errorDescription) {
    return null;
  }

  const searchParams = new URLSearchParams();

  if (error) {
    searchParams.set('error', error);
  }

  // URLSearchParams encodes the value, so even a malicious description is
  // inert: it can never break out into the path or smuggle a second
  // top-level query parameter.
  searchParams.set('error_description', errorDescription);

  return `/${Paths.loginCollision}?${searchParams.toString()}`;
};
